import React, { useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Description as TemplateIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/helpers';
import { useTheme } from '@mui/material/styles';
import { adjustOpacity } from '../../../../theme/utils';

/**
 * TemplateCard Component
 *
 * Displays a single template card in the selection grid.
 *
 * @param {Object} props
 * @param {Object} props.template - Template object
 * @param {boolean} props.isSelected - Whether this template is selected
 * @param {boolean} props.hasActiveExperiment - Whether template has active experiment
 * @param {Object} props.activeExperiment - Active experiment object
 * @param {Function} props.onSelect - Callback when template is selected
 */
function TemplateCard({
  template,
  isSelected,
  hasActiveExperiment,
  activeExperiment,
  onSelect,
}) {
  const theme = useTheme();

  // Format model configuration for tooltip
  const formatModelConfig = useCallback(() => {
    const rawConfig = template?.llm_config || template?.model_config;
    if (!rawConfig) {
      return { provider: 'N/A', model: 'N/A' };
    }

    let parsed = rawConfig;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { provider: 'N/A', model: 'N/A' };
      }
    }

    return {
      provider: parsed.provider || 'N/A',
      model: parsed.model_name || parsed.model || 'N/A',
      temperature: parsed.temperature,
      max_tokens: parsed.max_tokens,
      top_p: parsed.top_p,
      frequency_penalty: parsed.frequency_penalty,
      presence_penalty: parsed.presence_penalty,
    };
  }, [template]);

  // Create tooltip content for version chip
  const createVersionTooltip = useCallback(() => {
    const modelConfig = formatModelConfig();

    // Apple-style colors from theme
    const isDark = theme.palette.mode === 'dark';
    const bgColor = isDark
      ? theme.palette.grey[900]
      : theme.palette.background.paper;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const primaryColor = theme.palette.primary.main;
    const warningColor = theme.palette.warning.main;

    return (
      <Box
        sx={{
          p: 2.5,
          maxWidth: 380,
          bgcolor: bgColor,
          color: textPrimary,
          borderRadius: 2,
          boxShadow: theme.shadows[4],
          border: 'none',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            color: primaryColor,
            fontSize: '0.95rem',
            mb: 1.5,
            letterSpacing: '0.01em',
          }}
        >
          Model Configuration
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Provider:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.provider}
            </Box>
          </Typography>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Model:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.model}
            </Box>
          </Typography>
          {modelConfig.temperature !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Temperature:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.temperature}
              </Box>
            </Typography>
          )}
          {modelConfig.max_tokens !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Max Tokens:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.max_tokens}
              </Box>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }, [formatModelConfig, theme]);

  return (
    <Tooltip
      title={
        hasActiveExperiment
          ? `This template already has an active experiment: "${activeExperiment?.name || 'Unknown'}". Please stop or pause the existing experiment first.`
          : 'Click to select this template'
      }
      placement="top"
    >
      <Card
        variant="outlined"
        sx={{
          cursor: hasActiveExperiment ? 'not-allowed' : 'pointer',
          height: '100%',
          minHeight: 260,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: 2,
          border: isSelected ? 2 : 1,
          borderLeftWidth: isSelected ? 3 : 2,
          borderColor: hasActiveExperiment
            ? theme.palette.warning.main
            : isSelected
              ? theme.palette.primary.main
              : theme.palette.divider,
          // Keep background consistent between selected and unselected
          // so selection is indicated via border, accent bar, and chips only.
          backgroundColor: hasActiveExperiment
            ? theme.palette.action.disabledBackground
            : theme.palette.background.paper,
          opacity: hasActiveExperiment ? 0.6 : 1,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isSelected
            ? `0 4px 12px ${adjustOpacity(theme.palette.primary.main, 0.25)}`
            : `0 1px 3px ${adjustOpacity(theme.palette.common.black, 0.1)}`,
          '&:hover': {
            borderColor: hasActiveExperiment
              ? theme.palette.warning.main
              : theme.palette.primary.main,
            boxShadow: isSelected
              ? `0 6px 16px ${adjustOpacity(theme.palette.primary.main, 0.35)}`
              : `0 4px 12px ${adjustOpacity(theme.palette.common.black, 0.12)}`,
            transform: hasActiveExperiment ? 'none' : 'translateY(-2px)',
            backgroundColor: hasActiveExperiment
              ? theme.palette.action.disabledBackground
              : theme.palette.action.hover,
          },
        }}
        onClick={() => !hasActiveExperiment && onSelect(template)}
      >
        {/* Active Experiment indicator badge */}
        {hasActiveExperiment && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1,
              backgroundColor: 'warning.main',
              borderRadius: '50%',
              p: 0.75,
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon
              sx={{
                color: 'white',
                fontSize: 18,
              }}
            />
          </Box>
        )}

        {/* Selected indicator badge */}
        {isSelected && !hasActiveExperiment && (
          <Box
            sx={{
              position: 'absolute',
              top: hasActiveExperiment ? 50 : 10,
              right: 10,
              zIndex: 1,
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.75,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon
              sx={{
                color: 'white',
                fontSize: 18,
              }}
            />
          </Box>
        )}

        <CardContent
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 2.5,
            '&:last-child': {
              pb: 2.5,
            },
          }}
        >
          {/* Template Name */}
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 600,
              mb: 0.75,
              fontSize: '0.9375rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              // Use primary color for selected title text, default otherwise
              color: isSelected
                ? theme.palette.primary.main
                : theme.palette.text.primary,
              pr: isSelected ? 4 : 0,
            }}
          >
            {template.name}
          </Typography>

          {/* Description - Only show if exists, no minHeight */}
          {template.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.25,
                fontSize: '0.8125rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4,
              }}
            >
              {template.description}
            </Typography>
          )}

          {/* Active Experiment Warning Chip */}
          {hasActiveExperiment && (
            <Alert
              severity="warning"
              icon={<BlockIcon />}
              sx={{
                mb: 1.25,
                py: 0.5,
                '& .MuiAlert-message': {
                  fontSize: '0.75rem',
                  fontWeight: 500,
                },
              }}
            >
              Active experiment exists
            </Alert>
          )}

          {/* Chips Section */}
          <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.25}>
            <Chip
              label={template.category || 'general'}
              size="small"
              sx={{
                width: 90,
                height: 24,
                fontWeight: 500,
                backgroundColor: adjustOpacity(
                  theme.palette.primary.main,
                  0.12
                ),
                color: theme.palette.primary.main,
                fontSize: '0.75rem',
                '& .MuiChip-label': {
                  px: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
            <Chip
              label={template.status?.toUpperCase() || 'DRAFT'}
              size="small"
              sx={{
                width: 90,
                height: 24,
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor:
                  template.status === 'active'
                    ? adjustOpacity(theme.palette.success.main, 0.18)
                    : template.status === 'draft'
                      ? theme.palette.action.hover
                      : adjustOpacity(theme.palette.warning.main, 0.2),
                color:
                  template.status === 'active'
                    ? theme.palette.success.dark
                    : template.status === 'draft'
                      ? theme.palette.text.secondary
                      : theme.palette.warning.dark,
                '& .MuiChip-label': {
                  px: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
            <Tooltip
              title={createVersionTooltip()}
              arrow
              placement="right"
              enterDelay={300}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    padding: 0,
                    maxWidth: 'none',
                  },
                },
                arrow: {
                  sx: {
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[900]
                        : theme.palette.background.paper,
                  },
                },
              }}
            >
              <Chip
                label={`v${template.version || 1}`}
                size="small"
                variant="outlined"
                sx={{
                  width: 90,
                  height: 24,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .MuiChip-label': {
                    px: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    transform: 'scale(1.05)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  },
                }}
              />
            </Tooltip>
          </Box>

          {/* Footer - Created Date - Push to bottom */}
          <Box
            sx={{
              mt: 'auto',
              pt: 1.25,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
              }}
            >
              <TemplateIcon sx={{ fontSize: 13, opacity: 0.7 }} />
              Created: {formatDate(template.created_at)}
            </Typography>
          </Box>
        </CardContent>

        {/* Selected Footer Banner */}
        {isSelected && !hasActiveExperiment && (
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.75rem',
              }}
            >
              Selected
            </Typography>
          </Box>
        )}

        {/* Disabled Footer Banner */}
        {hasActiveExperiment && (
          <Box
            sx={{
              backgroundColor: 'warning.main',
              color: 'warning.contrastText',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            <BlockIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.75rem',
              }}
            >
              Not Available
            </Typography>
          </Box>
        )}
      </Card>
    </Tooltip>
  );
}

export default TemplateCard;
